import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";

export type ReorderCarrier = "amazon" | "ups" | "fedex" | "usps" | "other";
export type ReorderStatus = "pending" | "in_transit" | "delivered" | "picked_up";

export interface ReorderLineItem {
  item_id: string;
  item_name: string;
  quantity: number;
}

export interface InventoryReorder {
  id: string;
  property_id: string;
  created_by: string | null;
  created_at: string;
  items_json: ReorderLineItem[];
  supplier_note: string | null;
  tracking_number: string | null;
  carrier: ReorderCarrier | null;
  order_reference: string | null;
  status: ReorderStatus;
  delivered_at: string | null;
  picked_up_at: string | null;
  picked_up_by_cleaner_id: string | null;
  pickup_confirmations_json: Record<string, boolean>;
  maintenance_issue_report_id: string | null;
}

export interface PendingReorderForPickup {
  id: string;
  items: ReorderLineItem[];
  carrier: string | null;
  tracking_number: string | null;
  supplier_note: string | null;
  pickup_confirmations_json: Record<string, boolean>;
}

function parseReorder(row: Record<string, unknown>): InventoryReorder {
  const items = Array.isArray(row.items_json) ? (row.items_json as ReorderLineItem[]) : [];
  const confirmations =
    row.pickup_confirmations_json && typeof row.pickup_confirmations_json === "object"
      ? (row.pickup_confirmations_json as Record<string, boolean>)
      : {};
  return {
    ...(row as InventoryReorder),
    items_json: items,
    pickup_confirmations_json: confirmations,
    maintenance_issue_report_id: (row.maintenance_issue_report_id as string | null) ?? null,
  };
}

export const inventoryReordersQueryKey = (propertyId: string | undefined) =>
  ["inventory-reorders", propertyId] as const;

export const pendingPickupReordersQueryKey = (propertyId: string | undefined) =>
  ["inventory-reorders-pending-pickup", propertyId] as const;

export const issueRestockReorderQueryKey = (issueReportId: string | undefined) =>
  ["issue-restock-reorder", issueReportId] as const;

/** Slice 21a — reorder linked to an Issues Restock path (any status, including picked_up). */
export function useIssueRestockReorder(issueReportId: string | undefined) {
  return useQuery({
    queryKey: issueRestockReorderQueryKey(issueReportId),
    queryFn: async () => {
      if (!issueReportId) return null;
      const { data, error } = await supabase
        .from("inventory_reorders")
        .select("*")
        .eq("maintenance_issue_report_id", issueReportId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ? parseReorder(data as Record<string, unknown>) : null;
    },
    enabled: !!issueReportId,
  });
}

export function useInventoryReorders(propertyId: string | undefined) {
  return useQuery({
    queryKey: inventoryReordersQueryKey(propertyId),
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_reorders")
        .select("*")
        .eq("property_id", propertyId)
        .neq("status", "picked_up")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => parseReorder(r as Record<string, unknown>));
    },
    enabled: !!propertyId,
  });
}

export function usePendingPickupReorders(propertyId: string | undefined) {
  return useQuery({
    queryKey: pendingPickupReordersQueryKey(propertyId),
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_reorders")
        .select("*")
        .eq("property_id", propertyId)
        .eq("status", "delivered");
      if (error) throw error;
      return (data ?? []).map((r) => {
        const parsed = parseReorder(r as Record<string, unknown>);
        return {
          id: parsed.id,
          items: parsed.items_json,
          carrier: parsed.carrier,
          tracking_number: parsed.tracking_number,
          supplier_note: parsed.supplier_note,
          pickup_confirmations_json: parsed.pickup_confirmations_json,
        } satisfies PendingReorderForPickup;
      });
    },
    enabled: !!propertyId,
    staleTime: 30_000,
  });
}

export interface CreateReorderInput {
  propertyId: string;
  items: ReorderLineItem[];
  supplierNote?: string | null;
  trackingNumber?: string | null;
  carrier?: ReorderCarrier | null;
  orderReference?: string | null;
  /** Slice 21a — link to Issues Restock */
  maintenanceIssueReportId?: string | null;
}

export function useCreateInventoryReorder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReorderInput) => {
      const hasTracking = !!(input.trackingNumber?.trim());
      const status: ReorderStatus = hasTracking ? "in_transit" : "pending";
      const { data: user } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("inventory_reorders")
        .insert({
          property_id: input.propertyId,
          created_by: user.user?.id ?? null,
          items_json: input.items,
          supplier_note: input.supplierNote?.trim() || null,
          tracking_number: input.trackingNumber?.trim() || null,
          carrier: input.carrier ?? null,
          order_reference: input.orderReference?.trim() || null,
          status,
          maintenance_issue_report_id: input.maintenanceIssueReportId ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;

      const itemIds = input.items.map((i) => i.item_id).filter(Boolean);
      if (itemIds.length > 0) {
        const { error: flagErr } = await supabase
          .from("inventory_items")
          .update({ pending_reorder: true })
          .eq("property_id", input.propertyId)
          .in("id", itemIds);
        if (flagErr) throw flagErr;
      }

      return data.id as string;
    },
    onSuccess: (_id, vars) => {
      queryClient.invalidateQueries({ queryKey: inventoryReordersQueryKey(vars.propertyId) });
      if (vars.maintenanceIssueReportId) {
        queryClient.invalidateQueries({
          queryKey: issueRestockReorderQueryKey(vars.maintenanceIssueReportId),
        });
      }
      invalidateInventoryQueries(queryClient, vars.propertyId);
    },
  });
}

export function useUpdateInventoryReorder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      issueReportId,
      trackingNumber,
      carrier,
      orderReference,
      status,
    }: {
      id: string;
      propertyId: string;
      issueReportId?: string | null;
      trackingNumber?: string | null;
      carrier?: ReorderCarrier | null;
      orderReference?: string | null;
      status?: ReorderStatus;
    }) => {
      const patch: Record<string, unknown> = {};
      if (trackingNumber !== undefined) {
        patch.tracking_number = trackingNumber?.trim() || null;
        if (trackingNumber?.trim() && !status) {
          patch.status = "in_transit";
        }
      }
      if (carrier !== undefined) patch.carrier = carrier;
      if (orderReference !== undefined) {
        patch.order_reference = orderReference?.trim() || null;
      }
      if (status) patch.status = status;
      if (status === "delivered") patch.delivered_at = new Date().toISOString();

      const { error } = await supabase.from("inventory_reorders").update(patch).eq("id", id);
      if (error) throw error;

      if (status === "delivered") {
        try {
          const { notifyOwnerRestockDelivered } = await import("@/lib/insertRestockDeliveredNotification");
          await notifyOwnerRestockDelivered(id);
        } catch (e) {
          console.warn("[useUpdateInventoryReorder] delivered notify failed", e);
        }
      }

      return { propertyId, issueReportId };
    },
    onSuccess: ({ propertyId, issueReportId }) => {
      queryClient.invalidateQueries({ queryKey: inventoryReordersQueryKey(propertyId) });
      queryClient.invalidateQueries({ queryKey: pendingPickupReordersQueryKey(propertyId) });
      queryClient.invalidateQueries({ queryKey: ["owner-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["owner-notifications-unread-count"] });
      if (issueReportId) {
        queryClient.invalidateQueries({ queryKey: issueRestockReorderQueryKey(issueReportId) });
      }
    },
  });
}

export function useConfirmReorderPickup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reorderId,
      cleanerId,
      propertyId,
    }: {
      reorderId: string;
      cleanerId: string;
      propertyId: string;
    }) => {
      const { error } = await supabase.rpc("confirm_reorder_pickup", {
        reorder_id_in: reorderId,
        cleaner_id_in: cleanerId,
      });
      if (error) throw error;
      return propertyId;
    },
    onSuccess: (propertyId) => {
      queryClient.invalidateQueries({ queryKey: inventoryReordersQueryKey(propertyId) });
      queryClient.invalidateQueries({ queryKey: pendingPickupReordersQueryKey(propertyId) });
      invalidateInventoryQueries(queryClient, propertyId);
    },
  });
}

export function useConfirmSingleItemPickup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reorderId,
      itemId,
      qty,
      cleanerId,
      propertyId,
    }: {
      reorderId: string;
      itemId: string;
      qty: number;
      cleanerId: string;
      propertyId: string;
    }) => {
      const { error } = await supabase.rpc("confirm_single_item_pickup", {
        reorder_id_in: reorderId,
        item_id_in: itemId,
        qty_in: qty,
        cleaner_id_in: cleanerId,
      });
      if (error) throw error;
      return propertyId;
    },
    onSuccess: (propertyId) => {
      queryClient.invalidateQueries({ queryKey: inventoryReordersQueryKey(propertyId) });
      queryClient.invalidateQueries({ queryKey: pendingPickupReordersQueryKey(propertyId) });
      invalidateInventoryQueries(queryClient, propertyId);
    },
  });
}

export async function pollDeliveryTracking(reorderId?: string) {
  const { data, error } = await supabase.functions.invoke("poll-delivery-tracking", {
    body: reorderId ? { reorderId } : {},
  });
  if (error) throw error;
  return data;
}
